import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { useOrg } from '../OrgProvider';
import { fetchApiKeys, createApiKey, revokeApiKey } from '../api';
import type { ApiKey, ApiKeyCreateResponse } from '../types';
import { API_KEY_SCOPE_OPTIONS } from '@/lib/api-key-scopes';
import { canManageOrg } from '../org-roles';

function buildKeyMcpConfig(mcpUrl: string, key: string): string {
  return `{
  "mcpServers": {
    "llmcontext": {
      "url": "${mcpUrl}",
      "headers": { "Authorization": "Bearer ${key}" }
    }
  }
}`;
}

export function ApiKeysPanel() {
  const { getToken, deploymentMode } = useAuth();
  const { activeOrg } = useOrg();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  // Scopes beyond the ones every key gets (see API_KEY_SCOPE_OPTIONS).
  const [extraScopes, setExtraScopes] = useState<Set<string>>(new Set());
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const canManage = canManageOrg(activeOrg);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const mcpUrl = deploymentMode === 'saas' ? 'https://mcp.llmcontext.dev' : 'https://your-mcp-host';

  const copyKeyConfig = async (key: string) => {
    try {
      await navigator.clipboard.writeText(buildKeyMcpConfig(mcpUrl, key));
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const load = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (token) setKeys(await fetchApiKeys(activeOrg.id, token));
    } finally {
      setLoading(false);
    }
  }, [activeOrg, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!activeOrg || !newKeyName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const scopes = API_KEY_SCOPE_OPTIONS.filter((o) => o.always || extraScopes.has(o.scope)).map(
        (o) => o.scope
      );
      const result = await createApiKey(activeOrg.id, { name: newKeyName.trim(), scopes }, token);
      setCreatedKey(result);
      setNewKeyName('');
      setExtraScopes(new Set());
      await load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create the key.');
    } finally {
      setCreating(false);
    }
  }

  function toggleScope(scope: string, on: boolean) {
    setExtraScopes((prev) => {
      const next = new Set(prev);
      if (on) next.add(scope);
      else next.delete(scope);
      return next;
    });
  }

  async function handleRevoke(keyId: string) {
    if (!activeOrg) return;
    const token = await getToken();
    if (!token) return;
    await revokeApiKey(activeOrg.id, keyId, token);
    await load();
  }

  if (!activeOrg) {
    return <p className="text-sm text-dim">Select an organization first.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Create key form */}
      <form onSubmit={(e) => void handleCreate(e)} className="space-y-3">
        <h3 className="text-sm font-semibold text-ink">Create API Key</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Key name (e.g. production)"
            className="lc-input flex-1 py-1.5"
          />
          <Button type="submit" size="sm" disabled={creating || !newKeyName.trim()}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-dim">Scopes</legend>
          {API_KEY_SCOPE_OPTIONS.map((o) => {
            const locked = o.always || (o.adminOnly && !canManage);
            return (
              <label
                key={o.scope}
                className={`flex items-start gap-2 text-sm ${locked && !o.always ? 'opacity-60' : ''}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={o.always || extraScopes.has(o.scope)}
                  disabled={creating || locked}
                  onChange={(e) => toggleScope(o.scope, e.target.checked)}
                />
                <span>
                  <span className="font-medium text-ink">{o.label}</span>
                  <span className="block text-xs text-dim">
                    {o.description}
                    {o.always ? ' Always included.' : ''}
                    {o.adminOnly && !canManage
                      ? ' Only organization owners and admins can grant it.'
                      : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>
        {createError && (
          <p className="text-sm text-del" role="alert">
            {createError}
          </p>
        )}
      </form>

      {/* Newly created key banner */}
      {createdKey && (
        <div className="border border-good/30 bg-good/15 p-4">
          <p className="text-sm font-medium text-good">
            API key created! Copy it now — it won't be shown again.
          </p>
          <code className="mt-2 block break-all rounded bg-good/15 px-3 py-2 text-xs font-mono text-good">
            {createdKey.key}
          </code>
          {createdKey.scopes.includes('write') && (
            <p className="mt-3 text-xs text-dim">
              This is a CI key: store it as the{' '}
              <code className="font-mono text-2xs">LLMCONTEXT_API_KEY</code> secret of the
              repository that runs the llmcontext indexer, and nowhere else.
            </p>
          )}
          <p className="mt-3 text-xs text-dim">
            To use it, add this to your agent's MCP config (replaces the public no-key entry):
          </p>
          <pre className="mt-2 overflow-x-auto whitespace-pre rounded border border-rule bg-code-bg px-3 py-2 text-xs font-mono leading-[1.55] text-ink">
            {buildKeyMcpConfig(mcpUrl, createdKey.key)}
          </pre>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => void copyKeyConfig(createdKey.key)}
              className="text-xs font-medium text-accent underline"
            >
              {copiedConfig ? 'Copied!' : 'Copy MCP config'}
            </button>
            <button
              type="button"
              onClick={() => setCreatedKey(null)}
              className="text-xs text-good underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Keys list */}
      {loading ? (
        <p className="text-sm text-dim">Loading keys…</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-dim">No API keys yet.</p>
      ) : (
        <div className="overflow-hidden border border-rule">
          <table className="min-w-full text-sm">
            <thead className="bg-bg">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-dim">Name</th>
                <th className="px-4 py-2 text-left font-medium text-dim">Prefix</th>
                <th className="px-4 py-2 text-left font-medium text-dim">Scopes</th>
                <th className="px-4 py-2 text-left font-medium text-dim">Created</th>
                <th className="px-4 py-2 text-right font-medium text-dim">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule-soft">
              {keys.map((k) => (
                <tr key={k.id} className={k.revoked_at ? 'opacity-50' : ''}>
                  <td className="px-4 py-2 text-ink">{k.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-dim">{k.key_prefix}…</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {k.scopes.map((s) => (
                        <span key={s} className="rounded bg-bg px-1.5 py-0.5 text-xs text-dim">
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-dim">
                    {new Date(k.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {k.revoked_at ? (
                      <span className="text-xs text-del">Revoked</span>
                    ) : !canManage ? null : (
                      <button
                        type="button"
                        onClick={() => void handleRevoke(k.id)}
                        className="text-xs text-del hover:text-del/80"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
